import { jsx } from "react/jsx-runtime";
import { TextField, TextFieldTheme } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import React__default, { useRef, useCallback, useEffect } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useFocusFieldEvent } from "../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useTranslation } from "../../contexts/translation-context.js";
import { useFocus } from "../../hooks/use-focus.js";
import { classes } from "../../ui/number-input-field/number-input-field-wut.st.css";
import { localizeNumberInput, normalizeDecimalSeparator, isNumberInputPossible, getNumberOfDecimals, isNumberInputValid } from "../../ui/shared/numeric-input-utils.js";
import { useInvokeCallback } from "../../ui/shared/use-invoke-callback.js";
const NumberInputWUT = ({
  value,
  validation,
  required,
  label,
  disabled,
  readOnly,
  placeholder,
  onChange,
  onBlur,
  onFocus,
  hasError,
  inputId,
  ariaDescribedBy,
  fieldId
}) => {
  const { t } = useTranslation();
  const { regionalFormat } = useConfig();
  const multipleOf = validation?.number?.multipleOf || 1;
  const maximum = validation?.number?.maximum;
  const minimum = validation?.number?.minimum;
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const _value = localizeNumberInput(String(value ?? ""), regionalFormat);
  const [inputValue, setInputValue] = React__default.useState(_value);
  const invokeBlur = useInvokeCallback(onBlur);
  const inputRef = useRef(null);
  useFocusFieldEvent(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, fieldId);
  const numberOnChange = useCallback(
    (event) => {
      const userInput = event.target.value;
      const normalizedValue = normalizeDecimalSeparator(userInput);
      const isValid = isNumberInputPossible(normalizedValue, multipleOf);
      if (isValid) {
        setInputValue(userInput);
      }
    },
    [multipleOf]
  );
  const handleBlur = useCallback(() => {
    const normalizedValue = normalizeDecimalSeparator(inputValue);
    const newValue = Number(normalizedValue);
    if (inputValue === "" || Number.isNaN(newValue)) {
      onChange("");
      invokeBlur();
      return;
    }
    const numberOfDecimals = getNumberOfDecimals(multipleOf);
    const validValue = isNumberInputValid(newValue, multipleOf) ? newValue : newValue.toFixed(numberOfDecimals);
    onChange(Number(validValue));
    setInputValue(localizeNumberInput(String(validValue), regionalFormat));
    invokeBlur();
  }, [inputValue, multipleOf, onChange, invokeBlur, regionalFormat]);
  useEffect(() => {
    setInputValue(_value);
  }, [_value]);
  const { handleFocus, containerRef } = useFocus({
    onFocus
  });
  return /* @__PURE__ */ jsx("div", { ref: containerRef, onFocus: handleFocus, onBlur: handleBlur, children: /* @__PURE__ */ jsx(
    TextField,
    {
      id: inputId,
      type: "text",
      inputMode: "decimal",
      error: hasError,
      newErrorMessage: true,
      value: inputValue,
      className: classNames(classes.textField, classes.theme, {
        [classes.error]: hasError,
        [classes.hover]: forcedHover
      }),
      onChange: numberOnChange,
      disabled,
      readOnly,
      placeholder,
      min: minimum ?? void 0,
      max: maximum ?? void 0,
      "aria-label": label,
      "aria-roledescription": t("field.number.aria-role-description"),
      theme: TextFieldTheme.Box,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": hasError,
      required,
      ref: inputRef
    }
  ) });
};
export {
  NumberInputWUT
};
//# sourceMappingURL=number-input-wut.js.map
